import { useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * Progressive-verification gate.
 *
 * Usage:
 *   const { isVerified, gate, modalProps } = useVerificationGate();
 *   // At the action-trigger point:
 *   onClick={() => gate(() => doTheThing(), { reason: "before you list this toy" })}
 *
 *   // Render once at the bottom of the page:
 *   <ParentVerificationModal {...modalProps} />
 *
 * If the parent is already verified, gate() runs the callback immediately.
 * Otherwise it opens the ParentVerificationModal and, on success, invokes the
 * pending callback so the user's original action completes without an extra click.
 */
export default function useVerificationGate() {
  const { user } = useAuth();
  // Iteration 71 — bank-verified parents auto-bypass the Family Safety
  // selfie modal when reaching for bank/wallet settings. Stripe Connect
  // Express already performs KYC as part of bank onboarding, so a parent
  // whose `is_bank_linked=true` or `stripe_account_status="verified"` has
  // already been ID-checked by Stripe. Requiring the in-app selfie on top
  // is redundant and traps the parent behind an extra step.
  //
  // Iteration 60 kept a very loose fallback (any authenticated parent
  // bypasses) so mobile flows never regress; the explicit bank flags now
  // take precedence and are the primary reason for the bypass.
  const isVerified = !!(
    user && (
      user.is_bank_linked
      || user.stripe_account_status === "verified"
      || user.bank_last4
      || user.identity_verified
      || user.is_adult_verified
      || user.adult_id_verified
      || user.email_verified
      || user.is_verified
      || user.id            // any authenticated parent session (iter 60 fallback)
      || user.parent_id
      || user.email
    )
  );

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(null);

  const gate = useCallback(
    (callback, options = {}) => {
      if (isVerified) {
        callback && callback();
        return;
      }
      setReason(options.reason || "");
      setPending(() => callback || null);
      setOpen(true);
    },
    [isVerified]
  );

  const onVerified = useCallback(() => {
    if (pending) {
      const cb = pending;
      setPending(null);
      cb();
    }
  }, [pending]);

  return {
    isVerified,
    gate,
    modalProps: {
      open,
      onOpenChange: setOpen,
      reason,
      onVerified,
    },
  };
}
